import { useLanguage } from "../i18n/LanguageContext";

function formatDate(dateValue) {
  if (!dateValue) return null;

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function AccountPage({
  session,
  entitlements,
  loadingEntitlements,
  startSubscriptionCheckout,
  openBillingPortal,
  goBackToHome,
}) {
  const { t } = useLanguage();
  
  const isSuperuser =
    entitlements?.is_superuser === true;

  const isPremium =
    entitlements?.has_premium_access === true;

  const isTrialing =
    entitlements?.is_trialing === true;

  const subscriptionStatus =
    entitlements?.subscription_status || "free";

  const paymentType =
    entitlements?.payment_type || null;

  const accessSource =
    entitlements?.access_source || null;

  const isManualAccess =
    accessSource === "manual";

  const isStripeAccess =
    accessSource === "stripe";

  const trialEnd =
    formatDate(entitlements?.trial_end);

  const currentPeriodEnd =
    formatDate(entitlements?.current_period_end);

  const cancelAtPeriodEnd =
    entitlements?.cancel_at_period_end === true;

  const questionsUsed =
    entitlements?.questions_used ?? 0;

  const questionsLimit =
    entitlements?.questions_limit ?? 50;

  const questionsRemaining =
    entitlements?.questions_remaining ?? 0;

  const email =
    session?.user?.email || "";

  const displayName =
    session?.user?.user_metadata?.full_name ||
    session?.user?.user_metadata?.username ||
    email?.split("@")?.[0] ||
    "Student";

  // =========================================================
  // PLAN LABEL
  // =========================================================

  const getPlanName = () => {
    if (isSuperuser) {
      return t("account.superUserPremium");
    }

    if (isTrialing) {
      return t("account.premiumTrial");
    }

    if (isManualAccess) {
      return t("account.manualPaidAccess");
    }

    if (
      subscriptionStatus === "active" &&
      paymentType === "monthly"
    ) {
      return t("account.premiumMonthly");
    }

    if (
      subscriptionStatus === "active" &&
      paymentType === "quarterly"
    ) {
      return t("account.premiumQuarterly");
    }

    if (subscriptionStatus === "past_due") {
      return t("account.premium");
    }

    return t("account.freePlan");
  };

  // =========================================================
  // PRICE LABEL
  // =========================================================

  const getPriceLabel = () => {
    if (isSuperuser) {
      return t("account.permanentAccess");
    }

    if (isTrialing) {
      return t("account.threeDayFreeTrial");
    }

    if (isManualAccess) {
      return t("account.noAutoRenewal");
    }

    if (paymentType === "monthly") {
      return t("account.perMonthPrice");
    }

    if (paymentType === "quarterly") {
      return t("account.perThreeMonthsPrice");
    }

    return t("account.freePrice");
  };

  // =========================================================
  // STATUS LABEL
  // =========================================================

  const getStatusLabel = () => {
    if (isSuperuser) {
      return t("account.active");
    }

    if (subscriptionStatus === "past_due") {
      return t("account.paymentIssue");
    }

    if (isTrialing) {
      return t("account.trialActive");
    }

    if (
      isManualAccess &&
      subscriptionStatus === "active"
    ) {
      return t("account.active");
    }

    if (
      subscriptionStatus === "active" &&
      cancelAtPeriodEnd
    ) {
      return t("account.cancellationScheduled");
    }

    if (subscriptionStatus === "active") {
      return t("account.active");
    }

    if (
      subscriptionStatus === "canceled" ||
      subscriptionStatus === "expired"
    ) {
      return t("account.ended");
    }

    return t("account.free");
  };

  const getStatusDescription = () => {
    if (isSuperuser) {
      return t("account.permanentPremiumDesc");
    }

    if (isTrialing) {
      return trialEnd
        ? t("account.trialEndsOn", {
            date: trialEnd,
          })
        : t("account.premiumTrialActive");
    }

    if (subscriptionStatus === "past_due") {
      return t("account.paymentPaused");
    }

    if (
      subscriptionStatus === "active" &&
      cancelAtPeriodEnd
    ) {
      return currentPeriodEnd
        ? t("account.subscriptionEndsOn", {
            date: currentPeriodEnd,
          })
        : t(
            "account.subscriptionCancellationScheduled"
          );
    }

    if (
      isManualAccess &&
      subscriptionStatus === "active" &&
      currentPeriodEnd
    ) {
      return t("account.manualPaidAccessDesc", {
        date: currentPeriodEnd,
      });
    }

    if (
      subscriptionStatus === "active" &&
      currentPeriodEnd
    ) {
      return t("account.subscriptionRenewsOn", {
        date: currentPeriodEnd,
      });
    }

    if (
      subscriptionStatus === "canceled" ||
      subscriptionStatus === "expired"
    ) {
      return t(
        "account.previousSubscriptionEnded"
      );
    }

    return t("account.usingFreePlan");
  };

  const showManageSubscription =
    !isSuperuser &&
    isStripeAccess &&
    (
      subscriptionStatus === "active" ||
      subscriptionStatus === "trialing" ||
      subscriptionStatus === "past_due"
    );

  const showUpgrade =
    !isSuperuser &&
    !isPremium &&
    subscriptionStatus !== "past_due";

  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.container}>

        {/* ===================================================
            HEADER
        =================================================== */}

        <div style={pageStyles.header}>
          <div>
            <button
              style={pageStyles.backButton}
              onClick={goBackToHome}
            >
              ← {t("account.backToHome")}
            </button>

            <h1 style={pageStyles.title}>
              {t("account.title")}
            </h1>

            <p style={pageStyles.subtitle}>
              {t("account.subtitle")}
            </p>
          </div>
        </div>

        {/* ===================================================
            ACCOUNT PROFILE
        =================================================== */}

        <section style={pageStyles.card}>
          <div style={pageStyles.sectionHeader}>
            <div>
              <div style={pageStyles.sectionEyebrow}>
                {t("account.account")}
              </div>

              <h2 style={pageStyles.sectionTitle}>
                {t("account.profile")}
              </h2>
            </div>
          </div>

          <div style={pageStyles.profileGrid}>
            <div style={pageStyles.profileAvatar}>
              {displayName
                ?.charAt(0)
                ?.toUpperCase()}
            </div>

            <div>
              <div style={pageStyles.profileName}>
                {displayName}
              </div>

              <div style={pageStyles.profileEmail}>
                {email}
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            CURRENT PLAN
        =================================================== */}

        <section style={pageStyles.card}>
          <div style={pageStyles.sectionHeader}>
            <div>
              <div style={pageStyles.sectionEyebrow}>
                {t("account.subscription")}
              </div>

              <h2 style={pageStyles.sectionTitle}>
                {t("account.currentPlan")}
              </h2>
            </div>

            <div
              style={{
                ...pageStyles.statusBadge,

                ...(subscriptionStatus === "past_due"
                  ? pageStyles.statusWarning
                  : isPremium || isSuperuser
                  ? pageStyles.statusActive
                  : pageStyles.statusFree),
              }}
            >
              {loadingEntitlements
                ? t("account.loading")
                : getStatusLabel()}
            </div>
          </div>

          <div style={pageStyles.planPanel}>
            <div>
              <div style={pageStyles.planName}>
                {getPlanName()}
              </div>

              <div style={pageStyles.planPrice}>
                {getPriceLabel()}
              </div>

              <div style={pageStyles.planDescription}>
                {getStatusDescription()}
              </div>
            </div>

            {showManageSubscription && (
              <button
                style={pageStyles.primaryButton}
                onClick={openBillingPortal}
              >
                {subscriptionStatus === "past_due"
                  ? t("account.updatePaymentMethod")
                  : t("account.manageSubscription")}
              </button>
            )}
          </div>

          {/* =================================================
              PLAN DETAILS
          ================================================= */}

          <div style={pageStyles.detailsGrid}>
            <div style={pageStyles.detailBox}>
              <div style={pageStyles.detailLabel}>
                {t("account.plan")}
              </div>

              <div style={pageStyles.detailValue}>
                {getPlanName()}
              </div>
            </div>

            <div style={pageStyles.detailBox}>
              <div style={pageStyles.detailLabel}>
                {t("account.status")}
              </div>

              <div style={pageStyles.detailValue}>
                {getStatusLabel()}
              </div>
            </div>

            <div style={pageStyles.detailBox}>
              <div style={pageStyles.detailLabel}>
                {t("account.billing")}
              </div>

              <div style={pageStyles.detailValue}>
                {isSuperuser
                  ? t("account.notApplicable")
                  : isTrialing
                  ? t("account.trial")
                  : isManualAccess
                  ? t("account.manualPayment")
                  : paymentType === "monthly"
                  ? t("account.monthly")
                  : paymentType === "quarterly"
                  ? t("account.quarterly")
                  : t("account.free")}
              </div>
            </div>

            <div style={pageStyles.detailBox}>
              <div style={pageStyles.detailLabel}>
                {isTrialing
                  ? t("account.trialEnds")
                  : isManualAccess || cancelAtPeriodEnd
                  ? t("account.accessUntil")
                  : t("account.nextBillingDate")}
              </div>

              <div style={pageStyles.detailValue}>
                {isSuperuser
                  ? t("account.permanent")
                  : isTrialing
                  ? trialEnd || "—"
                  : currentPeriodEnd || "—"}
              </div>
            </div>
          </div>
        </section>

        {isManualAccess && isPremium && (
          <section style={pageStyles.card}>
            <div style={pageStyles.sectionHeader}>
              <div>
                <div style={pageStyles.sectionEyebrow}>
                  {t("account.noAutoRenewal")}
                </div>

                <h2 style={pageStyles.sectionTitle}>
                  {t("account.renewManualAccess")}
                </h2>

                <p style={pageStyles.upgradeText}>
                  {t("account.noAutoRenewalDesc")}
                </p>
              </div>
            </div>

            <div style={pageStyles.manualRenewalNotice}>
              <div style={pageStyles.manualRenewalTitle}>
                {t("account.contactForRenewal")}
              </div>

              <div style={pageStyles.manualContactGrid}>
                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.address")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    319 Easton Rd, Willow Grove, PA 19090
                  </div>
                </div>

                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.phone")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    267-312-7018
                  </div>
                </div>

                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.wechat")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    Best-Massage-School
                  </div>
                </div>

                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.email")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    supports@acemassagehub.com
                  </div>
                </div>
              </div>

              <div style={pageStyles.manualRenewalText}>
                {t("account.renewManualAccessDesc")}
              </div>
            </div>
          </section>
        )}

        {/* ===================================================
            ACCESS SUMMARY
        =================================================== */}

        <section style={pageStyles.card}>
          <div style={pageStyles.sectionHeader}>
            <div>
              <div style={pageStyles.sectionEyebrow}>
                {t("account.learningAccess")}
              </div>

              <h2 style={pageStyles.sectionTitle}>
                {t("account.yourAccess")}
              </h2>
            </div>
          </div>

          <div style={pageStyles.accessGrid}>
            <div style={pageStyles.accessCard}>
              <div style={pageStyles.accessTitle}>
                {t("account.practiceQuestions")}
              </div>

              <div style={pageStyles.accessValue}>
                {isPremium || isSuperuser
                  ? t("account.unlimited")
                  : t("account.remaining", {
                      count: questionsRemaining,
                    })}
              </div>

              <div style={pageStyles.accessSubtext}>
                {isPremium || isSuperuser
                  ? t("account.fullQuestionBankAccess")
                  : t("account.freeQuestionsUsed", {
                      used: questionsUsed,
                      limit: questionsLimit,
                    })}
              </div>
            </div>

            <div style={pageStyles.accessCard}>
              <div style={pageStyles.accessTitle}>
                {t("account.wrongQuestions")}
              </div>

              <div style={pageStyles.accessValue}>
                {t("account.included")}
              </div>

              <div style={pageStyles.accessSubtext}>
                {t("account.wrongQuestionsDesc")}
              </div>
            </div>

            <div style={pageStyles.accessCard}>
              <div style={pageStyles.accessTitle}>
                {t("account.learningCenter")}
              </div>

              <div style={pageStyles.accessValue}>
                {entitlements?.can_use_learning_center
                  ? t("account.unlocked")
                  : t("account.premiumAccess")}
              </div>

              <div style={pageStyles.accessSubtext}>
                {t("account.learningCenterDesc")}
              </div>
            </div>

            <div style={pageStyles.accessCard}>
              <div style={pageStyles.accessTitle}>
                {t("account.vocabulary")}
              </div>

              <div style={pageStyles.accessValue}>
                {entitlements?.can_use_vocabulary
                  ? t("account.unlocked")
                  : t("account.premiumAccess")}
              </div>

              <div style={pageStyles.accessSubtext}>
                {t("account.vocabularyDesc")}
              </div>
            </div>
          </div>
        </section>

        {/* ===================================================
            UPGRADE
        =================================================== */}

        {showUpgrade && (
          <section
            style={{
              ...pageStyles.card,
              ...pageStyles.upgradeCard,
            }}
          >
            <div style={pageStyles.sectionHeader}>
              <div>
                <div style={pageStyles.sectionEyebrow}>
                  {t("account.premiumLabel")}
                </div>

                <h2 style={pageStyles.sectionTitle}>
                  {t("account.upgradeToPremium")}
                </h2>

                <p style={pageStyles.upgradeText}>
                  {t("account.upgradeDesc")}
                </p>
              </div>
            </div>

            <div style={pageStyles.pricingGrid}>

              {/* MONTHLY */}

              <div style={pageStyles.priceCard}>
                <div style={pageStyles.pricePlanName}>
                  {t("account.monthlyPlan")}
                </div>

                <div style={pageStyles.price}>
                  $21.99
                </div>

                <div style={pageStyles.pricePeriod}>
                  {t("account.perMonth")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.fullPremiumAccess")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.unlimitedAI")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.cancelAnytime")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.threeDayTrialEligible")}
                </div>

                <button
                  style={pageStyles.primaryButtonFull}
                  onClick={() =>
                    startSubscriptionCheckout("monthly")
                  }
                >
                  {t("account.startMonthly")}
                </button>
              </div>

              {/* QUARTERLY */}

              <div
                style={{
                  ...pageStyles.priceCard,
                  ...pageStyles.featuredPriceCard,
                }}
              >
                <div style={pageStyles.bestValueBadge}>
                  {t("account.mostValuable")}
                </div>

                <div style={pageStyles.pricePlanName}>
                  {t("account.quarterlyPlan")}
                </div>

                <div style={pageStyles.price}>
                  $59.97
                </div>

                <div style={pageStyles.pricePeriod}>
                  {t("account.everyThreeMonths")}
                </div>

                <div style={pageStyles.priceSavings}>
                  {t("account.equivalentMonthly")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.fullPremiumAccess")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.unlimitedAI")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.saveSixDollars")}
                </div>

                <div style={pageStyles.priceFeature}>
                  ✓ {t("account.threeDayTrialEligible")}
                </div>

                <button
                  style={pageStyles.primaryButtonFull}
                  onClick={() =>
                    startSubscriptionCheckout("quarterly")
                  }
                >
                  {t("account.startQuarterly")}
                </button>
              </div>
            </div>

            {/* MANUAL / ALTERNATIVE PAYMENT */}

            <div style={pageStyles.manualPaymentOption}>
              <div style={pageStyles.manualPaymentHeader}>
                <div>
                  <div style={pageStyles.manualPaymentEyebrow}>
                    {t("account.otherPaymentOptions")}
                  </div>

                  <div style={pageStyles.manualPaymentTitle}>
                    {t("account.preferAnotherPaymentMethod")}
                  </div>
                </div>

                <div style={pageStyles.manualPaymentBadge}>
                  {t("account.noAutoRenewal")}
                </div>
              </div>

              <div style={pageStyles.manualPaymentDescription}>
                {t("account.manualPaymentAvailable")}
              </div>

              <div style={pageStyles.manualPaymentMethods}>
                {t("account.acceptedPaymentMethods")}
              </div>

              <div style={pageStyles.manualPaymentContactGrid}>
                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.phone")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    267-312-7018
                  </div>
                </div>

                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.wechat")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    Best-Massage-School
                  </div>
                </div>

                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.email")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    supports@acemassagehub.com
                  </div>
                </div>

                <div>
                  <div style={pageStyles.manualContactLabel}>
                    {t("account.address")}
                  </div>

                  <div style={pageStyles.manualContactValue}>
                    319 Easton Rd, Willow Grove, PA 19090
                  </div>
                </div>
              </div>

              <div style={pageStyles.manualPaymentFootnote}>
                {t("account.manualPaymentNoAutoRenew")}
              </div>
            </div>
          </section>
        )}

        {/* ===================================================
            DATA / PROGRESS
        =================================================== */}

        <section style={pageStyles.noteCard}>
          <div style={pageStyles.noteIcon}>
            ✓
          </div>

          <div>
            <div style={pageStyles.noteTitle}>
              {t("account.progressSaved")}
            </div>

            <div style={pageStyles.noteText}>
              {t("account.progressSavedDesc")}
            </div>
          </div>
        </section>

        {/* ===================================================
            FOOTER
        =================================================== */}

        <div style={pageStyles.footer}>
          <button
            style={pageStyles.footerBackButton}
            onClick={goBackToHome}
          >
            ← {t("account.returnToHome")}
          </button>
        </div>
      </div>
    </div>
  );
}

const pageStyles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%)",
    padding: "30px 18px 60px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    color: "#172033",
  },

  container: {
    width: "100%",
    maxWidth: "1000px",
    margin: "0 auto",
  },

  header: {
    marginBottom: "24px",
  },

  backButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    padding: 0,
    marginBottom: "14px",
    fontWeight: 700,
  },

  title: {
    fontSize: "32px",
    margin: "0 0 8px",
    fontWeight: 800,
    letterSpacing: "-0.6px",
  },

  subtitle: {
    margin: 0,
    color: "#64748b",
    lineHeight: 1.6,
  },

  card: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "26px",
    marginBottom: "20px",
    boxShadow: "0 8px 28px rgba(15,23,42,0.05)",
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "22px",
  },

  sectionEyebrow: {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: 800,
    letterSpacing: "1.2px",
    marginBottom: "6px",
  },

  sectionTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: 800,
  },

  profileGrid: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  profileAvatar: {
    width: "54px",
    height: "54px",
    borderRadius: "16px",
    background: "#eaf1ff",
    color: "#2563eb",
    fontWeight: 800,
    fontSize: "22px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  profileName: {
    fontWeight: 800,
    fontSize: "18px",
    marginBottom: "4px",
  },

  profileEmail: {
    color: "#64748b",
    fontSize: "14px",
  },

  statusBadge: {
    borderRadius: "999px",
    padding: "7px 12px",
    fontSize: "12px",
    fontWeight: 800,
  },

  statusActive: {
    background: "#ecfdf3",
    color: "#15803d",
  },

  statusWarning: {
    background: "#fff7ed",
    color: "#c2410c",
  },

  statusFree: {
    background: "#f1f5f9",
    color: "#475569",
  },

  planPanel: {
    display: "flex",
    justifyContent: "space-between",
    gap: "24px",
    alignItems: "center",
    padding: "22px",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg, #f8fbff 0%, #f5f8ff 100%)",
    marginBottom: "22px",
  },

  planName: {
    fontSize: "24px",
    fontWeight: 800,
    marginBottom: "5px",
  },

  planPrice: {
    color: "#2563eb",
    fontSize: "17px",
    fontWeight: 700,
    marginBottom: "10px",
  },

  planDescription: {
    color: "#64748b",
    fontSize: "14px",
    lineHeight: 1.6,
    maxWidth: "600px",
  },

  primaryButton: {
    border: "none",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    padding: "12px 18px",
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  detailsGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
  },

  detailBox: {
    background: "#f8fafc",
    borderRadius: "12px",
    padding: "16px",
    border: "1px solid #edf2f7",
  },

  detailLabel: {
    color: "#64748b",
    fontSize: "11px",
    marginBottom: "7px",
  },

  detailValue: {
    fontWeight: 800,
    fontSize: "14px",
  },

  accessGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
  },

  accessCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "18px",
  },

  accessTitle: {
    fontSize: "13px",
    color: "#64748b",
    marginBottom: "8px",
  },

  accessValue: {
    fontSize: "18px",
    fontWeight: 800,
    marginBottom: "6px",
  },

  accessSubtext: {
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
  },

  upgradeCard: {
    background:
      "linear-gradient(135deg, #f8fbff 0%, #fff 55%, #faf7ff 100%)",
  },

  upgradeText: {
    color: "#64748b",
    lineHeight: 1.6,
    marginBottom: 0,
  },

  pricingGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "18px",
  },

  priceCard: {
    position: "relative",
    border: "1px solid #dbe3ee",
    borderRadius: "16px",
    padding: "24px",
    background: "#fff",
  },

  featuredPriceCard: {
    border: "2px solid #2563eb",
  },

  bestValueBadge: {
    position: "absolute",
    top: "-11px",
    right: "18px",
    background: "#2563eb",
    color: "#fff",
    borderRadius: "999px",
    padding: "5px 10px",
    fontSize: "10px",
    fontWeight: 800,
  },

  pricePlanName: {
    fontWeight: 800,
    fontSize: "18px",
    marginBottom: "10px",
  },

  price: {
    fontSize: "30px",
    fontWeight: 800,
  },

  pricePeriod: {
    color: "#64748b",
    fontSize: "13px",
    marginBottom: "8px",
  },

  priceSavings: {
    color: "#15803d",
    fontSize: "12px",
    fontWeight: 700,
    marginBottom: "14px",
  },

  priceFeature: {
    fontSize: "13px",
    marginBottom: "9px",
    color: "#475569",
  },

  primaryButtonFull: {
    marginTop: "14px",
    width: "100%",
    border: "none",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    padding: "12px 16px",
    fontWeight: 800,
    cursor: "pointer",
  },

  manualRenewalNotice: {
    border: "1px solid #dbe3ee",
    borderRadius: "14px",
    padding: "20px",
    background: "#f8fafc",
  },

  manualRenewalTitle: {
    fontSize: "16px",
    fontWeight: 800,
    marginBottom: "18px",
  },

  manualContactGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "18px",
  },

  manualContactLabel: {
    color: "#64748b",
    fontSize: "11px",
    marginBottom: "5px",
  },

  manualContactValue: {
    fontSize: "14px",
    fontWeight: 700,
    wordBreak: "break-word",
  },

  manualRenewalText: {
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #e2e8f0",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },

  noteCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    padding: "20px",
    borderRadius: "16px",
    border: "1px solid #dcefe4",
    background: "#f3fbf6",
    marginBottom: "20px",
  },

  noteIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    background: "#dcfce7",
    color: "#15803d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
  },

  noteTitle: {
    fontWeight: 800,
    marginBottom: "5px",
  },

  noteText: {
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.6,
  },

  footer: {
    textAlign: "center",
    marginTop: "26px",
  },

  footerBackButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontWeight: 700,
    cursor: "pointer",
  },

  manualPaymentOption: {
    marginTop: "22px",
    padding: "22px",
    border: "1px solid #dbe3ee",
    borderRadius: "16px",
    background:
      "linear-gradient(135deg, #f8fafc 0%, #f8fbff 100%)",
  },

  manualPaymentHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px",
    marginBottom: "12px",
    flexWrap: "wrap",
  },

  manualPaymentEyebrow: {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: 800,
    letterSpacing: "1px",
    marginBottom: "5px",
  },

  manualPaymentTitle: {
    fontSize: "18px",
    fontWeight: 800,
  },

  manualPaymentBadge: {
    background: "#eef2ff",
    color: "#4338ca",
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "11px",
    fontWeight: 800,
  },

  manualPaymentDescription: {
    color: "#475569",
    fontSize: "14px",
    lineHeight: 1.7,
    marginBottom: "10px",
  },

  manualPaymentMethods: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#2563eb",
    marginBottom: "20px",
  },

  manualPaymentContactGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "16px",
    paddingTop: "18px",
    borderTop: "1px solid #e2e8f0",
  },

  manualPaymentFootnote: {
    marginTop: "18px",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },
};